import { Autocomplete, Grid, InputLabel, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { CountryFlag } from "components/Common/CountrySelector";
import { HoverTooltip } from "components/Common/Tooltips";
import { getCode } from "country-list";
import {
	selectCustomerLocations,
	selectHardwareDeliveryCountries,
	setIsCustomerLocationDialogOpen,
} from "features/customer";
import { useAppSelector, useAuth } from "hooks/hooks";
import { useMemo } from "react";
import { CustomerLocation } from "types";
import { HardwareDeliveryCountryOption, HardwarePurchaseType } from "utilities/constants/enums";
import useRole from "utilities/roleUtils/roleCheck";
import { EditLocationsDialog } from "../NewLocationDialog";
import styles from "./DeliveryLocationSelector.module.scss";

interface DeliveryLocationSelectorProps {
	selectedDeliveryCountry: string;
	selectedDeliveryLocation: CustomerLocation | null;
	handleSelectDeliveryLocation: (location: CustomerLocation | null) => void;
	hardwarePurchaseType?: HardwarePurchaseType;
}

interface CustomerLocationForDropdown extends CustomerLocation {
	disabled?: boolean;
}

export const DeliveryLocationSelector = ({
	selectedDeliveryCountry,
	selectedDeliveryLocation,
	handleSelectDeliveryLocation,
	hardwarePurchaseType,
}: DeliveryLocationSelectorProps) => {
	const { dispatch } = useAuth();
	const customerLocations = useAppSelector(selectCustomerLocations);
	// const hardwarePurchaseType = useAppSelector(selectHardwarePurchaseType);
	const { isActionRole } = useRole();

	const NEW_LOCATION_CITY = "Add new location";
	const allowedOptions = useMemo(() => {
		if (!customerLocations.locations) {
			return [];
		}
		// Disable locations that are not norway IF not leasing
		const options = customerLocations.locations
			.map((location) =>
				mapAllowedOptions(location, selectedDeliveryCountry, hardwarePurchaseType),
			)
			.sort(sortOptions);
		return options;
	}, [customerLocations.locations, selectedDeliveryCountry, hardwarePurchaseType]);

	const handleOnSelectDeliveryLocation = (location: CustomerLocationForDropdown | null) => {
		if (location?.city === NEW_LOCATION_CITY) {
			dispatch(setIsCustomerLocationDialogOpen(true));
			return;
		}

		handleSelectDeliveryLocation(location);
	};

	// No locations + user cannot add locations => hide
	const hideCustomerLocations = allowedOptions.length === 0 && !isActionRole;

	if (hideCustomerLocations) {
		return null;
	}

	return (
		<>
			<InputLabel>
				<Typography variant="body1" display="inline" mr={1}>
					Selected location:
				</Typography>
			</InputLabel>
			<Grid container>
				<Grid item xs={6}>
					<Autocomplete
						size="small"
						fullWidth
						options={allowedOptions}
						disabled={allowedOptions.length === 0}
						renderOption={(props, option) => (
							<Options
								{...props}
								location={option}
								selectedDeliveryCountry={selectedDeliveryCountry}
							/>
						)}
						getOptionLabel={(option) => {
							return option.address;
						}}
						isOptionEqualToValue={(
							option: CustomerLocationForDropdown,
							value: CustomerLocationForDropdown,
						) => option.address === value.address}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="outlined"
								placeholder="Select delivery location"
							>
								{selectedDeliveryLocation && (
									<Typography variant="description" color="text.secondary">
										{selectedDeliveryLocation.address}
									</Typography>
								)}
							</TextField>
						)}
						onChange={(_, value) => handleOnSelectDeliveryLocation(value)}
						value={selectedDeliveryLocation}
					/>
				</Grid>
				{isActionRole && (
					<Grid item>
						<Typography
							title="Add / edit locations"
							onClick={() => dispatch(setIsCustomerLocationDialogOpen(true))}
							className={styles.addLocationButton}
						>
							Add / edit locations
						</Typography>
					</Grid>
				)}
			</Grid>
			<EditLocationsDialog />
		</>
	);
};

const Options = (
	props: React.HTMLAttributes<HTMLLIElement> & {
		location: CustomerLocationForDropdown;
		selectedDeliveryCountry: string;
	},
) => {
	const { location, ...rest } = props;
	const deliveryCountryAlternatives = useAppSelector(selectHardwareDeliveryCountries);

	const disabledText = useMemo(() => {
		if (!location.disabled) {
			return "";
		} else if (deliveryCountryAlternatives === HardwareDeliveryCountryOption.Any) {
			return `Change delivery country to use this location`;
		} else if (location.disabled) {
			return `Shipping to ${props.selectedDeliveryCountry} only`;
		} else {
			return "";
		}
	}, [deliveryCountryAlternatives, location.disabled, props.selectedDeliveryCountry]);

	return (
		<HoverTooltip title={disabledText} hide={!location.disabled} placement="left-start">
			<li
				{...rest}
				className={clsx(styles.option, {
					[styles.disabledOption]: location.disabled,
				})}
				onClick={location.disabled ? undefined : props.onClick}
			>
				<Grid container>
					<Grid item xs={1} mr={1} mt={1}>
						<CountryFlag countryCode={location.countryCode} coloredBackground={false} />
					</Grid>
					<Grid item xs={10}>
						<Grid container>
							<Typography variant="body1">{location.city}</Typography>
						</Grid>
						<Grid item>
							<Typography variant="description">
								{location.address}, {location.postalCode}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			</li>
		</HoverTooltip>
	);
};

const mapAllowedOptions = (
	location: CustomerLocation,
	selectedDeliveryCountry: string,
	hardwarePurchaseType?: HardwarePurchaseType,
) => {
	let disabled = true;

	// When in "Lease" mode, allow any country as long as the location's country matches the selected delivery country
	if (hardwarePurchaseType === HardwarePurchaseType.Lease) {
		// For lease mode, we need to match the location's country with the selected delivery country
		// The location.countryCode is like "NO", "SE", "CH", etc.
		// The selectedDeliveryCountry is like "Norway", "Sweden", "Switzerland", etc.
		// We need to convert the country name to country code for comparison

		// Validate that selectedDeliveryCountry is a valid string
		if (!selectedDeliveryCountry || typeof selectedDeliveryCountry !== "string") {
			// If no valid country is selected, disable all locations
			disabled = true;
		} else {
			// Handle enum values by converting them to country names
			let countryName = selectedDeliveryCountry;
			if (selectedDeliveryCountry === HardwareDeliveryCountryOption.Norway) {
				countryName = "Norway";
			} else if (selectedDeliveryCountry === HardwareDeliveryCountryOption.Sweden) {
				countryName = "Sweden";
			} else if (selectedDeliveryCountry === HardwareDeliveryCountryOption.Any) {
				// For "Any" option, allow all locations
				disabled = false;
				return { ...location, disabled };
			}

			const expectedCountryCode = getCode(countryName);
			if (expectedCountryCode) {
				disabled = location.countryCode !== expectedCountryCode;
			} else {
				// If we can't get a country code, disable it (safer than allowing all)
				disabled = true;
			}
		}
	} else {
		// For "Buy" mode, use the existing logic
		// Handle both enum values and string values
		if (
			selectedDeliveryCountry === HardwareDeliveryCountryOption.Norway ||
			selectedDeliveryCountry === "Norway"
		) {
			disabled = location.countryCode !== "NO";
		} else if (
			selectedDeliveryCountry === HardwareDeliveryCountryOption.Sweden ||
			selectedDeliveryCountry === "Sweden"
		) {
			disabled = location.countryCode !== "SE";
		}
	}

	return {
		...location,
		disabled,
	};
};

const sortOptions = (a: CustomerLocationForDropdown, b: CustomerLocationForDropdown) => {
	// First option is always the "Add new location" option
	if (a.city === "Add new location") {
		return -1;
	}
	if (b.city === "Add new location") {
		return 1;
	}
	if (a.disabled && !b.disabled) {
		return 1;
	}
	if (!a.disabled && b.disabled) {
		return -1;
	}
	if (a.countryCode === "NO" && b.countryCode !== "NO") {
		return -1;
	}
	if (a.countryCode !== "NO" && b.countryCode === "NO") {
		return 1;
	}

	return 0;
};
